import { type FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import VerticalStepForm from '@/components/UI/VerticalStepForm/VerticalStepForm';
import type { StepConfig } from '@/components/UI/VerticalStepForm/VerticalStepForm.model';
import { _isNil } from '@/littledash';
import { ApiService, ResponseType } from '@/support/ApiService';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { web as webRoute } from '@/support/route';
import { agentUrlSlugMap } from '@/components/Agents/AgentUtils';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOCX_ACCEPT = `.docx,${DOCX_MIME}`;

type StudyProtocolParserSetupValues = {
  // UploadField clears the value to null on error/remove; reflect that so a
  // null can't be silently treated as a string on submit. Holds the opaque
  // batch_api_id of the uploaded protocol — never a key or URL.
  protocol_batch_api_id: string | null;
};

// TODO: presign + PUT + kickoff are inlined here temporarily to keep this off
// the POC AgentSessionService. Move to a dedicated study-protocol-parser
// service (beside the SSE code) once its home is decided.

/**
 * Presigns an upload slot, PUTs the file to S3, and resolves to the server-minted batch_api_id.
 * The server owns the S3 key (built from the team in its security context plus the IDs it mints);
 * the client only ever holds the opaque batch id, which it later sends to kick off the parser.
 */
const uploadProtocol = async (file: File, signal?: AbortSignal): Promise<string> => {
  const presigned = await ApiService.call({
    endpoint: 'POST /api/v1/ai-agents/study-protocol-parser/uploads',
    body: { file_name: file.name },
    signal,
    options: {
      onError: { toast: false, throw: false, capture: true, slug: 'study-protocol-parser-presign-upload' },
    },
  });
  if (presigned.type !== ResponseType.Success) {
    throw new Error('Could not mint a presigned upload URL.');
  }
  const { url, batch_api_id } = presigned.body;

  // The presign call reports itself via ApiService; the direct-to-S3 PUT bypasses
  // it, so capture both its failure modes (network reject + non-2xx) to Sentry.
  let putResponse: Response;
  try {
    putResponse = await fetch(url, { method: 'PUT', body: file, signal });
  } catch (cause) {
    const error = new InVivoError('Study protocol S3 upload request failed', {
      cause: cause instanceof Error ? cause : new Error(String(cause)),
      slug: 'study-protocol-parser-s3-put',
      level: 'warning',
    });
    ExceptionHandler.captureException(error);
    throw error;
  }
  if (!putResponse.ok) {
    const error = new InVivoError(`Study protocol S3 upload failed (${putResponse.status})`, {
      slug: 'study-protocol-parser-s3-put',
      level: 'warning',
    });
    ExceptionHandler.captureException(error);
    throw error;
  }
  return batch_api_id;
};

/**
 * Agents-hub setup form for the Study Protocol Parser. A single step uploads a
 * .docx protocol to S3 (presign + PUT on file-add) and stores the resulting
 * url; submitting kicks off the parser session and navigates to its stream.
 */
const StudyProtocolParserSetupForm: FC = () => {
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (values: StudyProtocolParserSetupValues): Promise<void> => {
      // The required rule blocks submit while the upload is unset; guard anyway
      // so the null shape can never reach the request body.
      if (_isNil(values.protocol_batch_api_id)) {
        return;
      }
      const response = await ApiService.call({
        endpoint: 'POST /api/v1/ai-agents/study-protocol-parser',
        body: { batch_api_id: values.protocol_batch_api_id },
        options: {
          onError: {
            toast_message: 'Failed to start the protocol parser. Please try again.',
            throw: false,
            capture: true,
            slug: 'study-protocol-parser-create-session',
          },
        },
      });
      if (response.type === ResponseType.Success) {
        const sessionApiId = response.body.api_id;
        navigate(webRoute('agents.session', { slug: agentUrlSlugMap.study_protocol_parser, sessionApiId }));
      }
    },
    [navigate]
  );

  const steps: Array<StepConfig> = useMemo(
    () => [
      {
        id: 'protocol',
        title: 'Upload protocol',
        required: true,
        fields: [
          {
            type: 'upload',
            name: 'protocol_batch_api_id',
            label: 'Protocol document',
            accept: DOCX_ACCEPT,
            helpText: 'Supported formats: .docx',
            uploadFile: uploadProtocol,
            rules: { required: 'A protocol document is required' },
          },
        ],
      },
    ],
    []
  );

  return (
    <VerticalStepForm<StudyProtocolParserSetupValues>
      steps={steps}
      onSubmit={handleSubmit}
      description="Upload a study protocol document to parse it into a draft study"
      submitDescription="Will extract study data, groups, and animals from the uploaded protocol."
    />
  );
};

export default StudyProtocolParserSetupForm;
